import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Toolbar } from '@mui/material';

// Auth provider
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Layout components
import Header from './components/Common/Header';
import Sidebar from './components/Common/Sidebar';
import Footer from './components/Common/Footer';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import ImageGenerationHub from './components/ImageGenerationHub/ImageGenerationHub';
// import ReviewPage from './pages/ReviewPage'; // Remove or comment out old review page
import EnhancedReviewSubmitPage from './components/ReviewSubmitPage/EnhancedReviewSubmitPage'; // <<< IMPORT NEW PAGE
import ReviewPage from './pages/ReviewPage';
import AdminPage from './pages/AdminPage';
import ImageDetailPage from './pages/ImageDetailPage';
import EditImagePage from './pages/EditImagePage'; // New import for edit page
import ScheduledSubmissionsPage from './pages/ScheduledSubmissionsPage';
import SearchPage from './pages/SearchPage';
import NotFoundPage from './pages/NotFoundPage';
import UPCMigrationPage from './pages/UPCMigrationPage';
import CarouselBatch from './pages/CarouselBatch';
import MarketingCopyPage from './pages/MarketingCopyPage';

// Create theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  return isAuthenticated ? children : <Navigate to="/login" />;
};

// Admin route component
const AdminRoute = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  return isAuthenticated && user?.is_admin ? children : <Navigate to="/dashboard" />;
};

// Main App component
function AppContent() {
  const { isAuthenticated } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(true);
  
  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      
      <Header onMenuToggle={toggleDrawer} />
      
      {isAuthenticated && <Sidebar open={drawerOpen} />}
      
      <Box
        component="main"
        sx={{
            flexGrow: 1,
            p: 1.5,
            transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
            }),
        }}
      >
        <Toolbar /> {/* Spacer for fixed header */}
        
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          
          {/* Protected routes */}
          <Route path="/" element={<Navigate to="/dashboard" />} />
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          } />

          <Route path="/generate-mrhi" element={
            <ProtectedRoute>
              <ImageGenerationHub />
            </ProtectedRoute>
          } />

          {/* Update this route for the new enhanced review page */}
          <Route path="/manage-images" element={ // <<< EXAMPLE NEW PATH
            <ProtectedRoute>
              <EnhancedReviewSubmitPage /> {/* <<< USE NEW COMPONENT */}
            </ProtectedRoute>
          } />

          <Route path="/images/:id" element={
            <ProtectedRoute>
              <ImageDetailPage />
            </ProtectedRoute>
          } />

          <Route path="/images/:imageId/edit" element={ // <-- New route for editing
            <ProtectedRoute>
              <EditImagePage /> 
            </ProtectedRoute>
          } />

          <Route path="/scheduled-submissions" element={
            <ProtectedRoute>
              <ScheduledSubmissionsPage />
            </ProtectedRoute>
          } />
          <Route path="/upc-migration" element={
            <ProtectedRoute>
              <UPCMigrationPage />
            </ProtectedRoute>
          } />

          <Route path="/search" element={
            <ProtectedRoute>
              <SearchPage />
            </ProtectedRoute>
          } />

          <Route path="/carousel-batch" element={
            <ProtectedRoute>
              <CarouselBatch />
            </ProtectedRoute>
          } />

          <Route path="/marketing-copy" element={
            <ProtectedRoute>
              <MarketingCopyPage />
            </ProtectedRoute>
          } />

          {/* Admin routes */}
          <Route path="/admin" element={
            <AdminRoute>
              <AdminPage />
            </AdminRoute>
          } />
          
          {/* 404 route */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        
        <Footer />
      </Box>
    </Box>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <Router>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;